import { useAppDispatch, useAppSelector } from 'appState/store'
import { Layout } from 'components/Layout'
import { CSCreateUserModal } from 'features/userManagement/components/CSCreateUserModal'
import { UserList } from 'features/userManagement/components/userList'
import { OaiUserCreate } from 'features/userManagement/types/userTypes'
import { cppuCreateOaiUser, cppuUpdateOaiUser } from 'features/userManagement/userApi'
import { updateCurrentUser } from 'features/userManagement/userSlice'
import { useState } from 'react'
import { FaPlus } from 'react-icons/fa'
import { Button } from '@outbound-ai/ursa'
import styled from 'styled-components'
import { ColorsEnum, ColorsToneEnum } from 'types/cssTypes'
import { ErrorMessagesEnum } from 'types/errorTypes'
import { errorHandler } from 'utils/errors/errors'
import { notificator } from 'utils/notificator/notificator'

const AccountSettingsOrg = () => {
  const currentCustomer = useAppSelector((state) => state.auth.currentCustomer)

  const [showModal, setShowModal] = useState(false)
  const dispatch = useAppDispatch()

  const handleCreateUser = async (user: OaiUserCreate) => {
    const { username, email, familyName, givenName, permissionIds, practiceIds } = user
    try {
      const { userGuid } = await cppuCreateOaiUser({
        username,
        email,
        familyName,
        givenName,
        customerId: currentCustomer,
      })
      await cppuUpdateOaiUser({ userGuid, permissionIds, practiceIds })
      dispatch(
        updateCurrentUser({
          refresh: true,
          recentlyModifiedUserGuid: userGuid,
          recentlyModifiedUserEmail: email,
        }),
      )
      notificator.notify({
        type: 'success',
        heading: `User ${email} created successfully and invitation has been sent`,
      })
    } catch (error: any) {
      const errorMessage = error?.response?.data?.parameters?.[0]?.value
      const action = error?.config?.url?.includes('create') ? 'create' : 'update'
      const fallbackMessage = ErrorMessagesEnum.SomethingWentWrong
      const userMessage = `${errorMessage || fallbackMessage}. Unable to ${action} user`
      errorHandler.handleError(error, userMessage)
    } finally {
      setShowModal(false)
    }
  }

  return (
    <LayoutStyled>
      <Div>
        <h1>Organization Settings</h1>
      </Div>
      <div className="page-main">
        <aside className="page-menu">
          <details>
            <summary>User Management</summary>
          </details>
        </aside>
        <div className="page-content">
          <div className="header">
            <h2>All Users</h2>
            <div className="d-flex ">
              <Button variant="secondary" size="small" radius="rounded" onClick={() => setShowModal(true)}>
                <FaPlus /> Add New User
              </Button>
            </div>
          </div>
          <UserList />
        </div>
      </div>
      <CSCreateUserModal
        show={showModal}
        hide={() => setShowModal(false)}
        title="Add New User"
        createUser={handleCreateUser}
      />
    </LayoutStyled>
  )
}

const Div = styled.div`
  height: ${({ theme }) => theme.constants.pageTitleHeight};
  display: inline-flex;
  width: 100%;
  align-items: center;
  padding: 0 ${({ theme }) => theme.spacing.l};
  background: ${({ theme }) => theme.colors.neutral.lighter};
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey.lighter};
  margin-bottom: ${({ theme }) => theme.spacing.l};
  h1 {
    ${({ theme }) => theme.typography.h4.bold};
    color: ${({ theme }) => theme.colors.primary.neutral};
    padding-block: 0.75rem;
  }
`

const LayoutStyled = styled(Layout)`
  .page-main {
    max-width: 97rem;
    margin: auto;
    position: relative;
    display: flex;
    height: ${({ theme }) => theme.util.pixelToRem(1144)};
    max-height: 80vh;
    overflow-y: auto;
    border-radius: ${({ theme }) => theme.borderRadius.rounded};
    box-shadow: 0 4px 4px rgba(0, 0, 0, 0.05);
    border: ${({ theme }) => theme.borders.s.solid(ColorsEnum.grey, ColorsToneEnum.light)};

    .page-menu {
      border-right: ${({ theme }) => theme.borders.s.solid(ColorsEnum.grey, ColorsToneEnum.light)};
      padding: 1rem;
      color: ${({ theme }) => theme.colors.grey.darker};

      details {
        margin-bottom: 1rem;
        background: #eaf9f7;
        border-radius: 10px;
        padding: 0.8rem 1rem;
      }

      summary {
        list-style-type: none;
        display: flex;
        justify-content: space-between;
        gap: 1rem;
        align-items: center;
        white-space: nowrap;
      }

      summary::-webkit-details-marker {
        display: none;
      }
    }

    .page-content {
      padding: 2.25rem;
      padding-top: 1.5rem;
      width: 100%;

      .header {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        margin-bottom: 1.5rem;
        color: ${({ theme }) => theme.colors.primary.darker};

        > h2 {
          ${({ theme }) => theme.typography.h5.normal};
          font-weight: ${({ theme }) => theme.fontWeight.bold};
        }
      }
    }
  }
`

export default AccountSettingsOrg
